import { DataTable } from "@components/DataTable/DataTable"
import type { ILinkedAccount } from "@apis/accounts/types"
import type { ColumnDef } from "@tanstack/react-table"
import { Link } from "react-router"
import { SquareArrowOutUpRight } from "lucide-react"
import { useGetLinkedAccountsQuery } from "@apis/accounts/useAccounts"
import { useSortingState } from "@hooks/useSortingState"

type TGetLinkedAccountsProps = {
  account_id: string
}

export const LinkedAccounts = ({ account_id }: TGetLinkedAccountsProps) => {
  const getLinkedAccountsQuery = useGetLinkedAccountsQuery(account_id)
  const sorting = useSortingState([
    { id: "service_address.street", desc: true },
  ])

  const tableColumnsLinkedAccounts: ColumnDef<ILinkedAccount>[] = [
    {
      header: "Name",
      cell: ({ row }) => {
        const { first_name, last_name } = row.original
        return (
          <span>
            {first_name} {last_name}
          </span>
        )
      },
    },
    {
      accessorKey: "product_type_code",
      header: "Product Type Code",
    },
    {
      id: "service_address.street",
      header: "Service Address",
      accessorFn: (row) => row.service_address.street,
      cell: ({ row }) => {
        const { street, unit, city, state_code, zip } =
          row.original.service_address

        return (
          <span>
            {street}
            {unit && ` ${unit}`}, {city}, {state_code} {zip}
          </span>
        )
      },
    },
    {
      accessorKey: "market_code",
      header: "Market Code",
    },
    {
      accessorKey: "account_number",
      header: "Account Number",
    },
    {
      id: "view_account",
      cell: ({ row }) => {
        const rowData = row.original
        const href = `profiles/${rowData.membership_profile_id}/accounts/${rowData.account_id}`

        return (
          <Link
            to={href}
            target="blank"
            aria-label="View account details"
            className="tw:flex tw:items-center tw:gap-1 tw:text-black tw:hover:text-blue-600 tw:hover:no-underline"
          >
            <span className="tw:inline-block tw:text-black">View</span>
            <SquareArrowOutUpRight
              size={24}
              className="tw:inline-block tw:p-1"
            />
          </Link>
        )
      },
    },
  ]

  if (getLinkedAccountsQuery.isError) {
    return (
      <div className="tw:text-red-500">
        There was an error loading linked accounts. Error:{" "}
        {getLinkedAccountsQuery.error.message}
      </div>
    )
  }

  return (
    <div>
      {getLinkedAccountsQuery?.data?.length ? (
        <DataTable
          columns={tableColumnsLinkedAccounts}
          data={getLinkedAccountsQuery?.data}
          {...sorting}
          pending={getLinkedAccountsQuery.isPending}
          classes="tw:w-full"
        />
      ) : (
        <h2>No Other Accounts</h2>
      )}
    </div>
  )
}
